import React from 'react'
import { motion } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import type { SimplifiedSiteStatus } from '../../shared/api/executive-monitoring.api'
import { useResponsive } from '../responsive-layout/useResponsive'

interface SiteListProps {
  sites: SimplifiedSiteStatus[]
}

const statusConfigs = {
  working: {
    icon: '✅',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    gradient: 'from-emerald-400 to-emerald-500',
    label: 'Работает',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30'
  },
  problem: {
    icon: '⚠️',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    gradient: 'from-amber-400 to-amber-500',
    label: 'Проблема',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30'
  },
  not_working: {
    icon: '🔴',
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    gradient: 'from-rose-400 to-rose-500',
    label: 'Не работает',
    iconBg: 'bg-rose-100 dark:bg-rose-900/30'
  }
}

const listVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1
    }
  }
}

const itemVariants: Variants = {
  hidden: { 
    opacity: 0, 
    x: -20,
    scale: 0.95 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  },
  hover: {
    scale: 1.02,
    y: -2,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  },
  tap: {
    scale: 0.98
  }
}

export const SiteList: React.FC<SiteListProps> = ({ sites }) => {
  const { isMobile } = useResponsive()
  const containerRef = React.useRef<HTMLDivElement>(null)

  useGSAP(() => {
    if (containerRef.current && sites.length > 0) {
      const items = containerRef.current.querySelectorAll('.site-item')
      
      gsap.fromTo(items,
        {
          opacity: 0,
          x: -30,
          scale: 0.9
        },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          stagger: 0.05,
          duration: 0.5,
          ease: "back.out(1.7)",
          delay: 0.2
        }
      )
    }
  }, [sites])

  const getStatusConfig = (status: string) => {
    return statusConfigs[status as keyof typeof statusConfigs] || {
      icon: '❓',
      color: 'text-gray-600 dark:text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      border: 'border-gray-200 dark:border-gray-700',
      gradient: 'from-gray-400 to-gray-500',
      label: 'Неизвестно',
      iconBg: 'bg-gray-100 dark:bg-gray-800/50'
    }
  }

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

      if (diffHours < 24) {
        return date.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      })
    } catch {
      return '—'
    }
  }

  const formatProblemDuration = (dateString?: string) => {
    if (!dateString) return null
    
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      
      if (diffMins < 1) return 'Только что'
      if (diffMins < 60) return `${diffMins} мин.`
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)} ч.`
      return `${Math.floor(diffMins / 1440)} дн.`
    } catch {
      return null
    }
  }

  const safeSites = Array.isArray(sites) ? sites : []

  if (safeSites.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 text-gray-400 mb-4">
          <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Нет данных для отображения
        </p>
      </motion.div>
    )
  }

  return (
    <motion.div
      ref={containerRef}
      variants={listVariants}
      initial="hidden"
      animate="visible"
      className="space-y-3"
    >
      {safeSites.map((site, index) => {
        const config = getStatusConfig(site.status)
        const problemDuration = formatProblemDuration(site.problemSince)
        
        return (
          <motion.div
            key={site.id}
            className="site-item group"
            variants={itemVariants}
            whileHover="hover"
            whileTap="tap"
            custom={index}
          >
            <div className={`
              relative overflow-hidden rounded-2xl border-2 p-4
              transition-all duration-300
              ${config.bg} ${config.border}
              hover:shadow-lg hover:shadow-emerald-500/20
            `}>
              {/* Gradient background on hover */}
              <div className={`
                absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5
                transition-opacity duration-300 ${config.gradient}
              `} />
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              
              <div className="relative flex items-center justify-between gap-4">
                {/* Left side - Site info */}
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Status icon */}
                  <motion.div
                    whileHover={{ rotate: [0, 360] }}
                    transition={{ duration: 0.6 }}
                    className={`
                      flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center
                      ${config.iconBg} backdrop-blur-sm
                      shadow-sm
                    `}
                  >
                    <div className={`text-xl ${config.color}`}>
                      {config.icon}
                    </div>
                  </motion.div>
                  
                  {/* Site details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {site.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${config.color} ${config.iconBg} backdrop-blur-sm`}>
                          {config.label}
                        </span>
                        
                        {problemDuration && site.status !== 'working' && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {problemDuration}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right side - Time and actions */}
                <div className="flex flex-col items-end gap-1">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {formatTime(site.lastCheck)}
                  </div>
                  
                  {site.problemSince && !isMobile && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-xs px-2 py-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 backdrop-blur-sm"
                    >
                      с {site.problemSince.split('T')[0]}
                    </motion.div>
                  )}
                  
                  {/* Action button */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    onClick={() => console.log('View details:', site.id)}
                  >
                    Подробнее →
                  </motion.button>
                </div>
              </div>
              
              {/* Progress bar for problem duration */}
              {site.problemSince && site.status !== 'working' && (
                <div className="mt-3">
                  <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 2, ease: "easeOut" }}
                      className={`h-full bg-gradient-to-r ${config.gradient}`}
                    />
                  </div>
                </div>
              )}
              
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-16 h-16 -translate-y-1/2 translate-x-1/2">
                <div className={`w-full h-full rounded-full bg-gradient-to-br ${config.gradient} opacity-10 blur-xl`} />
              </div>
            </div>
          </motion.div>
        )
      })}
    </motion.div>
  )
}